import React from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import { DIGITAL_PRODUCT } from '../../../../constants/constants';
import dataAids from '../../../../constants/dataAids';
import ProductDetailAssets, { FEATURED_CAROUSEL } from '../../products/ProductDetailAssets';
import CheckoutButtonsWrapper from '../../products/details/CheckoutButtonsWrapper';
import { ProductReviews, ProductStarRating } from '../../products/Yotpo';
import CommonDetails from '../CommonDetails';
import Files from '../../products/details/Files';
import getStyles from '../getStyles';
import PricesAndShipping from '../../products/details/PricesAndShipping';
import SimilarProducts from '../SimilarProducts';
import ProductLabels from '../../products/details/ProductLabels';
import SocialSharing from '../../products/details/SocialSharing';
import { isDescriptionHtml } from '../../utils/ProductUtils';

const mobilePriceStyles = {
  'display': 'none',
  '@xs': {
    display: 'block'
  },
  '@lg': {
    display: 'none'
  }
};

const productStarsStyles = {
  'marginTop': 'small',
  '@xs': {
    marginTop: 'medium'
  },
  '@lg': {
    marginTop: 'small'
  }
};

function ProductDetail2(props) {
  const {
    Grid,
    Grid: { Cell }
  } = UX2.Component;
  const { Block, Text } = UX2.Element;
  const styles = getStyles(props);
  const { showSimilarProducts, similarProducts = [] } = props;

  return (
    <Block style={{ position: 'relative' }}>
      <ProductDetailAssets
        layout={ FEATURED_CAROUSEL }
        renderMode={ props.renderMode }
        product={ props.product }
        currentAssetIndex={ props.currentAssetIndex }
        onClickThumbnail={ props.onClickThumbnail }
        assetChangedByVariantSelection={ props.assetChangedByVariantSelection }
        assetsVisible={ 5 }
        fetching={ props.fetching || props.processing || props.inSession }
        pdpImageCropMethod={ props.pdpImageCropMethod }
        pdpImageShape={ props.pdpImageShape }
        pdpLayout={ props.pdpLayout }
      />
      <Block style={{ marginTop: '-8px' }}>
        <Grid size={ 12 }>
          <Cell pushSm={ 2 } xs={ 12 } sm={ 8 } md={ 8 } lg={ 5 }>
            <Block style={{ marginBottom: '0' }}>
              <UX2.Element.Heading.Middle
                data-aid={ dataAids.PRODUCT_NAME_RENDERED }
                tag='h1'
                children={ props.product.name }
                style={ styles.name }
                group='Group'
                featured
              />

              <ProductLabels product={ props.product } translate={ props.translate } />

              <Block style={ mobilePriceStyles }>
                <PricesAndShipping
                  prices={ props.determinePrice(false) }
                  product={ props.product || {} }
                  translate={ props.translate }
                />
              </Block>

              <ProductStarRating
                product={ props.product }
                containerClass='product-detail'
                renderMode={ props.renderMode }
                containerStyle={ productStarsStyles }
              />
              { props.productType === DIGITAL_PRODUCT && (
                <Files
                  translate={ props.translate }
                  files={ props.files }
                  wrapperStyle={{ '@xs-only': { marginBottom: '0' } }}
                />
              ) }
              { isDescriptionHtml(props.description) ? (
                <Text
                  data-aid={ dataAids.PRODUCT_DESCRIPTION_RENDERED }
                  dangerouslySetInnerHTML={{ __html: props.description }}
                  style={ styles.description.desktop }
                />
              ) : (
                <Text
                  richtext
                  data-aid={ dataAids.PRODUCT_DESCRIPTION_RENDERED }
                  children={ props.description }
                  style={ styles.description.desktop }
                />
              ) }
            </Block>
          </Cell>
          <Cell pushSm={ 2 } pushLg={ 0 } xs={ 12 } sm={ 8 } md={ 8 } lg={ 3 }>
            <Block style={{ marginBottom: 'large', minWidth: '100%' }}>
              <CommonDetails
                { ...props }
                showPricesAndShippint={ false }
                quantityFieldSize={ 2 }
                disableDetails={{ files: true }}
                hidePriceOnMobile={ true }
                showReviews={ false }
                showProductLabels={ false }
              />
            </Block>
            <CheckoutButtonsWrapper
              renderMode={ props.renderMode }
              category={ props.category }
              prices={ props.determinePrice() }
              { ...props.baseState }
              requiredFieldsBlank={ props.requiredFieldsBlank }
              isSelectedVariantInStock={ props.isSelectedVariantInStock }
              handleAddToCart={ props.handleAddToCart }
              handleApplePayClick={ props.handleApplePayClick }
              getOrderData={ props.getVariantData }
              phoneRequired={ props.stripeConfig.require_phone_number }
              isPaymentRequestEnabled={ props.stripeConfig.with_payment_request }
              isPoyntPaymentRequestEnabled={ props.poyntConfig.with_apple_pay }
              translate={ props.translate }
              stackButtons={ true }
              showFacebook={ props.showFacebook }
              showTwitter={ props.showTwitter }
              domainName={ props.domainName }
              setHasAttemptedPurchase={ props.setHasAttemptedPurchase }
              isStickyCheckoutFooterEnabled={ props.isStickyCheckoutFooterEnabled }
            />
            <SocialSharing
              showFacebook={ props.showFacebook }
              showTwitter={ props.showTwitter }
              domainName={ props.domainName }
              translate={ props.translate }
              renderMode={ props.renderMode }
            />
            { isDescriptionHtml(props.description) ? (
              <Text
                data-aid={ dataAids.PRODUCT_DESCRIPTION_RENDERED }
                dangerouslySetInnerHTML={{ __html: props.description }}
                style={ styles.description.mobile }
              />
            ) : (
              <Text
                richtext
                data-aid={ dataAids.PRODUCT_DESCRIPTION_RENDERED }
                children={ props.description }
                style={ styles.description.mobile }
              />
            ) }
          </Cell>
        </Grid>
      </Block>
      <Grid size={ 12 }>
        <Cell xs={ 12 }>
          <ProductReviews product={ props.product } renderMode={ props.renderMode } />
        </Cell>
      </Grid>
      { showSimilarProducts && similarProducts.length > 0 && <SimilarProducts { ...props } /> }
    </Block>
  );
}

ProductDetail2.propTypes = {
  assetChangedByVariantSelection: PropTypes.bool,
  baseState: PropTypes.object,
  category: PropTypes.string,
  currentAssetIndex: PropTypes.number,
  description: PropTypes.string,
  determinePrice: PropTypes.func,
  fetching: PropTypes.bool,
  files: PropTypes.array,
  handleAddToCart: PropTypes.func,
  handleApplePayClick: PropTypes.func,
  getVariantData: PropTypes.func,
  inSession: PropTypes.bool,
  isSelectedVariantInStock: PropTypes.bool,
  onClickThumbnail: PropTypes.func,
  pdpImageCropMethod: PropTypes.string,
  processing: PropTypes.bool,
  product: PropTypes.object,
  stripeConfig: PropTypes.object,
  poyntConfig: PropTypes.object,
  productType: PropTypes.string,
  renderMode: PropTypes.string,
  requiredFieldsBlank: PropTypes.bool,
  translate: PropTypes.func,
  pdpImageShape: PropTypes.string,
  showSimilarProducts: PropTypes.bool,
  similarProducts: PropTypes.array,
  pdpLayout: PropTypes.string.isRequired,
  showFacebook: PropTypes.bool,
  showTwitter: PropTypes.bool,
  domainName: PropTypes.string,
  setHasAttemptedPurchase: PropTypes.func,
  useBruce: PropTypes.bool,
  isStickyCheckoutFooterEnabled: PropTypes.bool
};

export default ProductDetail2;
